import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { formatDubai } from '../../../../utils/dubaiTime';

// 🔹 Simple time parser used only for grouping
function parseTimeToHour(timeString) {
    if (!timeString) return NaN;
    const timeMatch = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!timeMatch) return NaN;
    let [, hours, minutes, period] = timeMatch;
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    else if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
    return hours + minutes / 60;
}

function ScheduleCopyButton({
    selectedDate,
    areaLabel,
    timeGroupLabel,
    bookings,
    assignments,
    activeTimeGroup, // 'morning' | 'peak' | 'afternoon' | 'all'
    availabilities = [],
    hasSpotCleanerForArea = false,
    /** The driver assigned to this route, if any. */
    driverName = null,
}) {
    const [copied, setCopied] = useState(false);

    if (!bookings || bookings.length === 0) {
        return null;
    }

    // 🔹 Does this area need spot cleaner at all (any time of the day)?
    const areaNeedsSpotCleaner =
        hasSpotCleanerForArea || bookings.some((b) => b.has_spot_cleaner);

    // 🔹 Build quick maps for lookup
    const availabilityById = new Map();
    const availabilityByCleanerId = new Map();

    availabilities.forEach((a) => {
        if (a.availability_id != null) {
            availabilityById.set(a.availability_id, a);
        }
        if (a.cleaner_id != null) {
            availabilityByCleanerId.set(a.cleaner_id, a);
        }
    });

    const getAvailabilityForSlot = (slot) => {
        if (slot?.availability_id && availabilityById.has(slot.availability_id)) {
            return availabilityById.get(slot.availability_id);
        }
        if (slot?.cleaner_id && availabilityByCleanerId.has(slot.cleaner_id)) {
            return availabilityByCleanerId.get(slot.cleaner_id);
        }
        return null;
    };

    const formatCleanerLabel = (slot) => {
        if (!slot?.name) return null;

        const availability = getAvailabilityForSlot(slot);

        let label = slot.name;
        const flags = [];

        if (availability?.is_part_timer) flags.push('PT');
        if (availability?.is_trial) flags.push('Trial');

        if (flags.length > 0) {
            label += ` (${flags.join(', ')})`;
        }

        return label;
    };

    // ✅ Only show "Amenities: / Laundry:" for these booking type IDs
    const SERVICE_FLAG_TYPE_IDS = new Set([2, 3, 4, 9, 10]);

    // Fallback if ID is missing
    const SERVICE_FLAG_TYPE_NAMES = new Set([
        'Hourly Cleaning with Materials',
        'Hourly Cleaning without Materials',
        'Deep Cleaning',
        'Touchup Cleaning (Free)',
        'Minimum Call Out',
    ]);

    const shouldShowServiceFlags = (b) => {
        const id = Number(
            b.booking_type_id ??
            b.booking_typeId ??
            b.booking_type_id_fk ??
            b.booking_type_fk ??
            b.type_id
        );

        if (!Number.isNaN(id) && SERVICE_FLAG_TYPE_IDS.has(id)) return true;

        const name = (b.booking_type || '').trim();
        return SERVICE_FLAG_TYPE_NAMES.has(name);
    };

    const formatYesNo = (v) => (v ? 'Yes' : 'No');

    const handleCopy = async () => {
        try {
            // Asia/Dubai render (item 8) — same dd/mm/yyyy + weekday visuals.
            const dateStr = formatDubai(selectedDate, 'dd/MM/yyyy');
            const weekday = formatDubai(selectedDate, 'cccc');

            // HEADER LINE
            const header = `${dateStr}, ${weekday}, ${timeGroupLabel.toUpperCase()} – ${areaLabel}`;

            // Unique cleaners across these bookings (overall)
            const cleanersMap = new Map(); // key -> label

            bookings.forEach((b) => {
                const slots = assignments?.[b.id] || [];
                slots.forEach((slot) => {
                    const label = formatCleanerLabel(slot);
                    if (!label) return;

                    const key = slot.availability_id || `c-${slot.cleaner_id || slot.name}`;
                    if (!cleanersMap.has(key)) cleanersMap.set(key, label);
                });
            });

            const cleanerNames = Array.from(cleanersMap.values());
            const cleanersCount = cleanerNames.length;

            const summaryLabel = activeTimeGroup === 'all' ? 'All day' : timeGroupLabel;

            const cleanersLine = cleanerNames.length
                ? `${summaryLabel} Cleanings: ${cleanersCount} cleaners - ${cleanerNames.join(', ')}`
                : `${summaryLabel} Cleanings: 0 cleaners assigned`;

            const lines = [];
            lines.push(header);
            /**
             * WHO IS DRIVING (2026-07-28). The copied text is what gets
             * sent to the team, and it named the route and every cleaner but
             * not the one person collecting them. Its own line rather than
             * appended to the header: the header already carries date,
             * weekday, time band and route, and WhatsApp wraps it badly.
             */
            if (driverName) lines.push(`Driver: ${driverName}`);
            lines.push('');
            lines.push(cleanersLine);

            if (areaNeedsSpotCleaner) {
                lines.push('');
                lines.push('Prepare and Bring Spot Cleaning Machine');
            }

            lines.push('');

            const pushBookingLines = (bookingList) => {
                bookingList.forEach((b, i) => {
                    const index = i + 1;

                    const bedLabel = b.bedrooms === 0 ? 'S' : (b.bedrooms ?? '-');
                    const bathLabel =
                        b.bathrooms !== null && b.bathrooms !== undefined ? b.bathrooms : '-';

                    lines.push(
                        `${index}. ${b.client_name} - ${b.building_name} - Unit ${b.unit_number} (${bedLabel} - ${bathLabel})`
                    );

                    const bookingSlots = assignments?.[b.id] || [];
                    const bookingCleanerNames = bookingSlots
                        .map((s) => formatCleanerLabel(s))
                        .filter(Boolean);

                    const cleanersText = bookingCleanerNames.length
                        ? bookingCleanerNames.join(', ')
                        : 'Not assigned yet';

                    lines.push(`Cleaners: ${cleanersText}`);

                    if (b.has_spot_cleaner) {
                        lines.push('Spot Cleaning Machine Required');
                    }

                    const accessRaw = b.property_access || b.property_access_note || '';
                    const accessText =
                        accessRaw && String(accessRaw).trim() !== ''
                            ? String(accessRaw).trim()
                            : 'Not Provided';

                    lines.push(`Access: ${accessText}`);
                    lines.push(`Start Time: ${b.start_time}`);

                    const baseType = b.booking_type || 'Cleaning';
                    const showLinenHire = !!b.is_linen_hire && !!b.is_laundry;
                    const typeWithLinen = showLinenHire ? `${baseType} + Linen Hire` : baseType;

                    const durationText = b.duration ? `${b.duration} hours` : '';
                    const typeLine = durationText
                        ? `Type: ${typeWithLinen} (${durationText})`
                        : `Type: ${typeWithLinen}`;

                    lines.push(typeLine);

                    // ✅ This is the line you wanted
                    if (shouldShowServiceFlags(b)) {
                        lines.push(
                            `Amenities: ${formatYesNo(!!b.is_amenities)}, Laundry: ${formatYesNo(!!b.is_laundry)}`
                        );
                    }

                    const admins = [
                        b.account_manager_first_name,
                        b.account_coordinator_first_name,
                    ]
                        .map((v) => String(v || '').trim())
                        .filter(Boolean);

                    if (admins.length) {
                        lines.push(`Admins: ${admins.join(', ')}`);
                    }

                    lines.push('');
                });
            };

            if (activeTimeGroup !== 'all') {
                pushBookingLines(bookings);
            } else {
                const morning = [];
                const peak = [];
                const afternoon = [];

                bookings.forEach((b) => {
                    const h = parseTimeToHour(b.start_time);
                    if (isNaN(h)) {
                        peak.push(b);
                        return;
                    }
                    if (h < 10) morning.push(b);
                    else if (h >= 10 && h < 14) peak.push(b);
                    else afternoon.push(b);
                });

                if (morning.length > 0) {
                    lines.push('Morning:');
                    lines.push('');
                    pushBookingLines(morning);
                }

                if (peak.length > 0) {
                    lines.push('Peak Hours:');
                    lines.push('');
                    pushBookingLines(peak);
                }

                if (afternoon.length > 0) {
                    lines.push('Afternoon:');
                    lines.push('');
                    pushBookingLines(afternoon);
                }
            }

            const textToCopy = lines.join('\n');

            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy schedule:', err);
        }
    };

    return (
        <button
            type="button"
            className={`scheduling-copy-button${copied ? ' is-copied' : ''}`}
            onClick={handleCopy}
            title={copied ? 'Copied!' : 'Copy schedule for this area'}
        >
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        </button>
    );
}

export default ScheduleCopyButton;